'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { getBookingStats, getInquiryStats } from '@/services/supabaseService';

export default function AdminStats() {
  const [bookingStats, setBookingStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    thisMonth: 0,
  });
  const [inquiryStats, setInquiryStats] = useState({
    total: 0,
    new: 0,
    responded: 0,
    thisWeek: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const [bookingsResult, inquiriesResult] = await Promise.all([
        getBookingStats(),
        getInquiryStats(),
      ]);

      if (bookingsResult.error) {
        setError(bookingsResult.error);
      } else if (bookingsResult.data) {
        setBookingStats({
          total: Number(bookingsResult.data.total_bookings) || 0,
          pending: Number(bookingsResult.data.pending_bookings) || 0,
          confirmed: Number(bookingsResult.data.confirmed_bookings) || 0,
          thisMonth: Number(bookingsResult.data.this_month_bookings) || 0,
        });
      }

      if (inquiriesResult.error) {
        setError(inquiriesResult.error);
      } else if (inquiriesResult.data) {
        setInquiryStats({
          total: Number(inquiriesResult.data.total_inquiries) || 0,
          new: Number(inquiriesResult.data.new_inquiries) || 0,
          responded: Number(inquiriesResult.data.responded_inquiries) || 0,
          thisWeek: Number(inquiriesResult.data.this_week_inquiries) || 0,
        });
      }
    } catch (err: any) {
      console.error('Error loading stats:', err);
      setError('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-component-id="src/app/admin/components/AdminStats.tsx:68:6" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="68" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div data-component-id="src/app/admin/components/AdminStats.tsx:70:10" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="70" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20shadow-warm%20p-6%20border%20border-border%20animate-pulse%22%7D"
            key={i}
            className="bg-card rounded-lg shadow-warm p-6 border border-border animate-pulse"
          >
            <div data-component-id="src/app/admin/components/AdminStats.tsx:74:12" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="74" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-muted%20rounded%20w-3%2F4%20mb-4%22%7D" className="h-6 bg-muted rounded w-3/4 mb-4" />
            <div data-component-id="src/app/admin/components/AdminStats.tsx:75:12" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="75" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-10%20bg-muted%20rounded%20w-1%2F2%22%7D" className="h-10 bg-muted rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div data-component-id="src/app/admin/components/AdminStats.tsx:84:6" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="84" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-destructive%2F10%20border%20border-destructive%20rounded-lg%20p-6%22%7D" className="bg-destructive/10 border border-destructive rounded-lg p-6">
        <div data-component-id="src/app/admin/components/AdminStats.tsx:85:8" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="85" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
          <Icon data-component-id="src/app/admin/components/AdminStats.tsx:86:10" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="86" data-component-file="AdminStats.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ExclamationTriangleIcon%22%2C%22className%22%3A%22text-destructive%22%7D" name="ExclamationTriangleIcon" size={24} className="text-destructive" />
          <div data-component-id="src/app/admin/components/AdminStats.tsx:87:10" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="87" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <p data-component-id="src/app/admin/components/AdminStats.tsx:88:12" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="88" data-component-file="AdminStats.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-destructive%20font-body%20font-semibold%22%2C%22textContent%22%3A%22Fehler%20beim%20Laden%20der%20Statistiken%22%7D" className="text-destructive font-body font-semibold">Fehler beim Laden der Statistiken</p>
            <p data-component-id="src/app/admin/components/AdminStats.tsx:89:12" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="89" data-component-file="AdminStats.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-destructive%2F80%20font-body%20text-sm%20mt-1%22%7D" className="text-destructive/80 font-body text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    {
      title: 'Gesamtbuchungen',
      value: bookingStats.total,
      icon: 'CalendarIcon',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Ausstehende Buchungen',
      value: bookingStats.pending,
      icon: 'ClockIcon',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
    },
    {
      title: 'Neue Anfragen',
      value: inquiryStats.new,
      icon: 'EnvelopeIcon',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Dieser Monat',
      value: bookingStats.thisMonth,
      icon: 'ChartBarIcon',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
  ];

  return (
    <div data-component-id="src/app/admin/components/AdminStats.tsx:128:4" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="128" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat, index) => (
        <div data-component-id="src/app/admin/components/AdminStats.tsx:130:8" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="130" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20shadow-warm%20p-6%20border%20border-border%20hover%3Ashadow-lg%20transition-all%20duration-fast%22%7D"
          key={index}
          className="bg-card rounded-lg shadow-warm p-6 border border-border hover:shadow-lg transition-all duration-fast"
        >
          <div data-component-id="src/app/admin/components/AdminStats.tsx:134:10" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="134" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-4%22%7D" className="flex items-center justify-between mb-4">
            <div data-component-id="src/app/admin/components/AdminStats.tsx:135:12" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="135" data-component-file="AdminStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}
            >
              <Icon data-component-id="src/app/admin/components/AdminStats.tsx:138:14" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="138" data-component-file="AdminStats.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={stat.icon as any} size={24} className={stat.color} />
            </div>
          </div>
          <p data-component-id="src/app/admin/components/AdminStats.tsx:141:10" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="141" data-component-file="AdminStats.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20font-body%20text-sm%20mb-1%22%7D" className="text-muted-foreground font-body text-sm mb-1">{stat.title}</p>
          <p data-component-id="src/app/admin/components/AdminStats.tsx:142:10" data-component-path="src/app/admin/components/AdminStats.tsx" data-component-line="142" data-component-file="AdminStats.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-foreground%20font-headline%20text-3xl%20font-bold%22%7D" className="text-foreground font-headline text-3xl font-bold">{stat.value}</p>
        </div>
      ))}
    </div>
  );
}